import { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import { TreeSelect } from "antd";
import SelectBox from "components/elements/selectBox";
import { getCategories } from "actions/categoryAction";

const formatCategoryTree = (nodes = [], parentLabel = "") => {
    return nodes.map((node) => {
        const title = node?.local_name || node?.title || "";
        const fullLabel = parentLabel ? `${parentLabel} > ${title}` : title;

        return {
            value: node?.id,
            label: fullLabel,
            title,
            fullLabel,
            disabled: node?.is_leaf === false,
            children: formatCategoryTree(node?.children || [], fullLabel),
        };
    });
};

function ModalChangeCategory({ onSubmit, products_selected, getCategories }) {
    const [isOpen, setIsOpen] = useState(false);
    const [category, setCategory] = useState("");
    const [categoryVersion, setCategoryVersion] = useState("v2");
    const [categories, setCategories] = useState([]);
    const [loadingCategory, setLoadingCategory] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (!isOpen) return;

        setLoadingCategory(true);
        getCategories(null, categoryVersion)
            .then((data) => {
                setCategories(formatCategoryTree(data || []));
            })
            .finally(() => {
                setLoadingCategory(false);
            });
    }, [categoryVersion, getCategories, isOpen]);

    const onClose = () => {
        setIsOpen(false);
    };

    const handleSubmit = () => {
        onSubmit({ category, category_version: categoryVersion });
        setCategory("");
        onClose();
    };

    return (
        <Fragment>
            <button
                className="btn btn-sm btn-primary me-2 mb-2"
                data-bs-toggle="modal"
                data-bs-target="#kt_modal_create_app"
                id="kt_toolbar_primary_button"
                onClick={() => setIsOpen(true)}
                disabled={products_selected <= 0}
            >
                <i className="ki-outline ki-category fs-2"></i>
                {t("Change Category")} {products_selected}
            </button>
            <Modal
                centered={true}
                size="lg"
                toggle={() => {
                    onClose();
                }}
                isOpen={isOpen}
            >
                <div className="modal-header">
                    <h2 className="mb-0 text-info">{t("Change Category")}</h2>
                    <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
                        <i
                            className="ki-outline ki-cross fs-1"
                            onClick={() => {
                                onClose();
                            }}
                        ></i>
                    </div>
                </div>
                <ModalBody>
                    <div className="form-group mb-5">
                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                            <span className="required">{t("Category Version")}</span>
                        </label>
                        <SelectBox
                            options={[
                                { value: "v1", label: "v1" },
                                { value: "v2", label: "v2" },
                            ]}
                            name="category_version"
                            value={categoryVersion}
                            onChange={(data) => {
                                setCategoryVersion(data?.value || "v2");
                                setCategory("");
                            }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                            <span className="required">{t("Category")}</span>
                        </label>
                        <TreeSelect
                            showSearch
                            allowClear
                            treeLine
                            style={{ width: "100%" }}
                            dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                            placeholder={t("Please select")}
                            treeNodeFilterProp="fullLabel"
                            treeNodeLabelProp="fullLabel"
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            notFoundContent={loadingCategory ? t("Loading...") : t("No data!")}
                            onChange={(value) => {
                                setCategory(value || "");
                            }}
                            treeData={categories}
                            value={category || undefined}
                            disabled={loadingCategory}
                        />
                    </div>
                    <div className="form-text mt-3">
                        {t("Product attributes will be reset after changing category.")}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="reset"
                        id="kt_modal_users_search_reset"
                        data-bs-dismiss="modal"
                        className="btn btn-active-light me-3"
                        onClick={() => {
                            onClose();
                        }}
                    >
                        {t("Cancel")}
                    </button>
                    <button
                        onClick={handleSubmit}
                        type="submit"
                        id="kt_modal_users_search_submit"
                        className="btn btn-primary"
                        disabled={!category}
                    >
                        {t("Save")}
                    </button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
}

const mapStateToProps = (state) => ({});
const mapDispatchToProps = { getCategories };

export default connect(mapStateToProps, mapDispatchToProps)(ModalChangeCategory);
